import type { RawEditorOptions } from 'tinymce';
import {
  FONT_SIZE_OPTIONS,
  TOOLBAR_GROUPS,
  ToolbarButtons,
  ToolbarGroups,
} from './registry';

type StyleMap = Record<string, string>;

const FONT_SIZES_PT = FONT_SIZE_OPTIONS.map(option => parseFloat(option.value));

const PX_TO_PT = 0.75;

const LEGACY_FONT_SIZES: Record<string, string> = {
  '1': '8pt',
  '2': '10pt',
  '3': '12pt',
  '4': '14pt',
  '5': '18pt',
  '6': '24pt',
  '7': '36pt',
};

const ALIGNMENT_BUTTONS: readonly string[] =
  TOOLBAR_GROUPS[ToolbarGroups.Alignment];

const STYLE_TAGS: {
  button: ToolbarButtons;
  tag: string;
  matches: (styles: StyleMap) => boolean;
}[] = [
  {
    button: ToolbarButtons.Bold,
    tag: 'strong',
    matches: styles =>
      styles['font-weight'] === 'bold' || Number(styles['font-weight']) >= 600,
  },
  {
    button: ToolbarButtons.Italic,
    tag: 'em',
    matches: styles => styles['font-style'] === 'italic',
  },
  {
    button: ToolbarButtons.Underline,
    tag: 'u',
    matches: styles => !!styles['text-decoration']?.includes('underline'),
  },
  {
    button: ToolbarButtons.Strikethrough,
    tag: 's',
    matches: styles => !!styles['text-decoration']?.includes('line-through'),
  },
  {
    button: ToolbarButtons.Superscript,
    tag: 'sup',
    matches: styles => styles['vertical-align'] === 'super',
  },
  {
    button: ToolbarButtons.Subscript,
    tag: 'sub',
    matches: styles => styles['vertical-align'] === 'sub',
  },
];

/** Converts a pt/px size to the nearest allowed font size, e.g. 11pt → 10.5pt, 16px → 12pt. */
const toAllowedFontSize = (value: string): string | null => {
  const match = /^([\d.]+)(pt|px)$/.exec(value.trim());
  if (!match) return null;

  const size =
    match[2] === 'px' ? parseFloat(match[1]) * PX_TO_PT : parseFloat(match[1]);
  const nearest = FONT_SIZES_PT.reduce((closest, option) =>
    Math.abs(option - size) < Math.abs(closest - size) ? option : closest
  );
  return `${nearest}pt`;
};

const filterStyles = (styles: StyleMap, toolbarButtons: string[]): StyleMap => {
  const allowed: StyleMap = {};
  const has = (button: string) => toolbarButtons.includes(button);

  if (styles['text-align'] && ALIGNMENT_BUTTONS.some(has)) {
    allowed['text-align'] = styles['text-align'];
  }
  if (styles['font-size'] && has(ToolbarButtons.FontSize)) {
    const fontSize = toAllowedFontSize(styles['font-size']);
    if (fontSize) allowed['font-size'] = fontSize;
  }
  if (styles.color && has(ToolbarButtons.ForeColor)) {
    allowed.color = styles.color;
  }
  if (
    styles['background-color'] &&
    styles['background-color'] !== 'transparent' &&
    has(ToolbarButtons.BackColor)
  ) {
    allowed['background-color'] = styles['background-color'];
  }
  return allowed;
};

const wrapChildren = (el: HTMLElement, tag: string) => {
  const wrapper = el.ownerDocument.createElement(tag);
  while (el.firstChild) wrapper.appendChild(el.firstChild);
  el.appendChild(wrapper);
};

/**
 * Creates a paste handler that keeps only the formatting the editor's toolbar
 * lets the user set. Legacy `align`/`<font>` attributes and style-based
 * formatting (Google Docs bold, italic...) are converted first.
 */
export const createPastePostProcess =
  (toolbarButtons: string[]): RawEditorOptions['paste_postprocess'] =>
  (editor, args) => {
    const { dom } = editor;

    args.node
      .querySelectorAll('b[id^="docs-internal-guid"]')
      .forEach(el => dom.remove(el, true));

    args.node
      .querySelectorAll<HTMLElement>('[align]:not(table):not(img)')
      .forEach(el => {
        dom.setStyle(el, 'text-align', el.getAttribute('align'));
        el.removeAttribute('align');
      });

    args.node.querySelectorAll<HTMLElement>('font').forEach(el => {
      const legacySize = LEGACY_FONT_SIZES[el.getAttribute('size') ?? ''];
      if (legacySize) dom.setStyle(el, 'font-size', legacySize);
      el.removeAttribute('size');
      el.removeAttribute('face');
      if (!toolbarButtons.includes(ToolbarButtons.ForeColor)) {
        el.removeAttribute('color');
      }
    });

    args.node.querySelectorAll<HTMLElement>('[style]').forEach(el => {
      const styles: StyleMap = dom.parseStyle(dom.getAttrib(el, 'style'));
      STYLE_TAGS.filter(
        ({ button, matches }) =>
          toolbarButtons.includes(button) && matches(styles)
      ).forEach(({ tag }) => wrapChildren(el, tag));

      const allowedStyles = filterStyles(styles, toolbarButtons);
      dom.setAttrib(el, 'style', dom.serializeStyle(allowedStyles));
    });

    const tableStyles = editor.options.get('table_default_styles');
    const tableAttributes = editor.options.get('table_default_attributes');
    args.node.querySelectorAll('table').forEach(table => {
      dom.setStyles(table, tableStyles);
      dom.setAttribs(table, tableAttributes);
    });
  };
